import React, { useState, useEffect } from "react";
import { LookupInput, type LookupInputOption, type LookupInputState } from "@/components/ui/LookupInput";
import { listService } from "@/services/listService";

interface PositionInputProps {
  value: string | null;
  state?: LookupInputState;
  label?: string;
  error?: string;
  placeholder?: string;
  optionsSource?: LookupInputOption[];
  onChange?: (positionId: string | null) => void;
}

export function PositionInput({
  value,
  state = "editing",
  label = "Position",
  error,
  optionsSource,
  placeholder = "Select position\u2026",
  onChange,
}: PositionInputProps) {
  const [options, setOptions] = useState<LookupInputOption[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (optionsSource) {
      setOptions(optionsSource);
      setLoading(false);
      return;
    }

    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        const positions = await listService.getPositions();
        if (cancelled) return;
        setOptions(
          positions.map((p) => ({
            id: p.id!,
            name: p.name,
            subtitle: p.description ?? undefined,
            icon: "badge-account-horizontal" as const,
          })),
        );
      } catch (err) {
        console.error("Failed to load positions:", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [optionsSource]);

  const resolvedState: LookupInputState = loading ? "loading" : state;

  return (
    <LookupInput
      label={label}
      value={value}
      options={options}
      state={resolvedState}
      icon="badge-account-horizontal"
      error={error}
      placeholder={placeholder}
      onChange={onChange}
    />
  );
}
